import { forwardRef } from 'react'
import { Menu } from '@base-ui/react/menu'
import { cva, type VariantProps } from 'class-variance-authority'
import { twMerge } from 'tailwind-merge'
import { listItemVariants } from '../../../lib/list-styles'

/**
 * `default` and `danger` are the row every list shares. The tinted variants are kept for the few
 * call sites that use them; reach for `danger` or a plain row before adding a new one.
 */
export const menuItemVariants = cva('', {
  variants: {
    variant: {
      default: listItemVariants({ tone: 'neutral' }),
      danger: listItemVariants({ tone: 'danger' }),
      primary: twMerge(
        listItemVariants({ tone: 'neutral' }),
        'text-brand-subtle data-[highlighted]:bg-brand-subtle data-[highlighted]:text-brand',
      ),
      bold: twMerge(listItemVariants({ tone: 'neutral' }), 'bg-brand text-on-brand data-[highlighted]:bg-brand-hover'),
      success: twMerge(
        listItemVariants({ tone: 'neutral' }),
        'text-success-subtle data-[highlighted]:bg-success-subtle data-[highlighted]:text-success',
      ),
    },
  },
  defaultVariants: {
    variant: 'default',
  },
})

export type MenuItemVariant = VariantProps<typeof menuItemVariants>['variant']

export interface MenuItemProps extends Menu.Item.Props, VariantProps<typeof menuItemVariants> {
  className?: string
}

/** A row that runs an action and closes the menu. Same row as Select's, from `lib/list-styles`. */
const MenuItem = forwardRef<HTMLDivElement, MenuItemProps>(function MenuItem(
  { variant, className, children, ...props },
  ref,
) {
  return (
    <Menu.Item ref={ref} data-slot="list-item" className={twMerge(menuItemVariants({ variant }), className)} {...props}>
      {children}
    </Menu.Item>
  )
})

export { MenuItem }
